function resolveApiBase() {
  const fromGlobal = globalThis && globalThis.__API_BASE_URL__ ? String(globalThis.__API_BASE_URL__) : "";
  const fromImportMeta = import.meta.env && import.meta.env.VITE_API_BASE_URL ? String(import.meta.env.VITE_API_BASE_URL) : "";
  const base = (fromGlobal || fromImportMeta || "http://localhost:3001").trim();
  return base.replace(/\/+$/, "");
}

export const API_BASE = resolveApiBase();

/** Lista de testes na API. Requer sessão: o servidor exige autenticação em GET /api/tests. */
export async function fetchTestsFromApi({ authToken } = {}) {
  const res = await fetch(API_BASE + "/api/tests", {
    headers: {
      ...(authToken ? { authorization: `Bearer ${authToken}` } : {})
    }
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error((data && data.error) || `API error ${res.status}`);
  return Array.isArray(data) ? data : [];
}

export async function login({ email, password }) {
  const res = await fetch(API_BASE + "/api/auth/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, password })
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error((data && data.error) || `API error ${res.status}`);
  return data;
}

export async function publishTestsToApi({ tests, explanations = null, replaceAll = false, authToken = null, adminSharedToken = null }) {
  const res = await fetch(API_BASE + "/api/admin/tests/import", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      ...(authToken ? { authorization: `Bearer ${authToken}` } : {}),
      ...(adminSharedToken ? { "x-admin-token": adminSharedToken } : {})
    },
    body: JSON.stringify({ tests, explanations, replaceAll })
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error((data && data.error) || `API error ${res.status}`);
  return data;
}

/** Remove um teste da API (Mongo) e as suas explicações. */
export async function deleteTestFromApi({ testId, authToken = null, adminSharedToken = null }) {
  const res = await fetch(API_BASE + "/api/admin/tests/" + encodeURIComponent(String(testId)), {
    method: "DELETE",
    headers: {
      ...(authToken ? { authorization: `Bearer ${authToken}` } : {}),
      ...(adminSharedToken ? { "x-admin-token": adminSharedToken } : {})
    }
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error((data && data.error) || `API error ${res.status}`);
  return data;
}

/** Remove só as explicações na API; o documento do teste mantém-se. */
export async function deleteTestExplanationsFromApi({ testId, authToken = null, adminSharedToken = null }) {
  const res = await fetch(
    API_BASE + "/api/admin/tests/" + encodeURIComponent(String(testId)) + "/explanations",
    {
      method: "DELETE",
      headers: {
        ...(authToken ? { authorization: `Bearer ${authToken}` } : {}),
        ...(adminSharedToken ? { "x-admin-token": adminSharedToken } : {})
      }
    }
  );
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error((data && data.error) || `API error ${res.status}`);
  return data;
}

async function authedFetch(path, { method = "GET", authToken, body } = {}) {
  const res = await fetch(API_BASE + path, {
    method,
    headers: {
      ...(authToken ? { authorization: `Bearer ${authToken}` } : {}),
      ...(body ? { "content-type": "application/json" } : {})
    },
    body: body ? JSON.stringify(body) : undefined
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error((data && data.error) || `API error ${res.status}`);
  return data;
}

export async function fetchAdminTestsCatalog({ authToken }) {
  return await authedFetch("/api/admin/tests", { authToken });
}

export async function fetchAdminTestsExport({ authToken }) {
  return await authedFetch("/api/admin/tests/export", { authToken });
}

export async function fetchAdminTestFull({ authToken, testId }) {
  const tid = String(testId || "").trim();
  if (!tid) throw new Error("Missing testId");
  return await authedFetch(`/api/admin/tests/${encodeURIComponent(tid)}/full`, { authToken });
}

export async function fetchAdminTestsAttemptStats({ authToken, testIds }) {
  const ids = (Array.isArray(testIds) ? testIds : []).map((x) => String(x || "")).filter(Boolean);
  if (!ids.length) return { byTestId: {} };
  const qs = encodeURIComponent(ids.join(","));
  return await authedFetch(`/api/admin/tests/stats?ids=${qs}`, { authToken });
}

const ADMIN_EXPL_IDS_CHUNK = 200;

/** Contagem de documentos de explicação por testId (Mongo). */
export async function fetchAdminExplanationCounts({ authToken, testIds }) {
  const ids = (Array.isArray(testIds) ? testIds : []).map((x) => String(x || "")).filter(Boolean);
  if (!ids.length) return { byTestId: {} };
  const byTestId = {};
  for (let i = 0; i < ids.length; i += ADMIN_EXPL_IDS_CHUNK) {
    const slice = ids.slice(i, i + ADMIN_EXPL_IDS_CHUNK);
    const qs = encodeURIComponent(slice.join(","));
    const r = await authedFetch(`/api/admin/explanations/counts?ids=${qs}`, { authToken });
    const part = (r && r.byTestId) || {};
    Object.assign(byTestId, part);
  }
  return { byTestId };
}

/** Mapa testId → { qn: explanation } a partir do Mongo (vários testes). */
export async function fetchAdminExplanationsBulk({ authToken, testIds }) {
  const ids = (Array.isArray(testIds) ? testIds : []).map((x) => String(x || "")).filter(Boolean);
  if (!ids.length) return {};
  const out = {};
  for (let i = 0; i < ids.length; i += ADMIN_EXPL_IDS_CHUNK) {
    const slice = ids.slice(i, i + ADMIN_EXPL_IDS_CHUNK);
    const qs = encodeURIComponent(slice.join(","));
    const r = await authedFetch(`/api/admin/explanations/bulk?ids=${qs}`, { authToken });
    const part = (r && r.explanations) || {};
    Object.assign(out, part);
  }
  return out;
}

/** Grava todas as explicações de um teste no Mongo (upsert por pergunta). */
export async function saveAdminTestExplanations({ authToken, testId, byQn, purgeMissing = false }) {
  const tid = String(testId || "").trim();
  if (!tid) throw new Error("Missing testId");
  return await authedFetch(`/api/admin/tests/${encodeURIComponent(tid)}/explanations`, {
    method: "POST",
    authToken,
    body: { byQn: byQn && typeof byQn === "object" ? byQn : {}, purgeMissing: !!purgeMissing }
  });
}

export async function getAdminUsers({ authToken }) {
  return await authedFetch("/api/admin/users", { authToken });
}

export async function createAdminUser({ authToken, firstName, lastName, email, role }) {
  return await authedFetch("/api/admin/users", {
    method: "POST",
    authToken,
    body: {
      firstName: String(firstName || ""),
      lastName: String(lastName || ""),
      email: String(email || ""),
      role: role === "superadmin" ? "superadmin" : role === "admin" ? "admin" : role === "reviewer" ? "reviewer" : "user"
    }
  });
}

export async function getAdminUser({ authToken, userId }) {
  return await authedFetch(`/api/admin/users/${encodeURIComponent(String(userId))}`, { authToken });
}

export async function setUserSuspended({ authToken, userId, suspended }) {
  return await authedFetch(`/api/admin/users/${encodeURIComponent(String(userId))}/suspend`, {
    method: "POST",
    authToken,
    body: { suspended: !!suspended }
  });
}

export async function deleteUser({ authToken, userId }) {
  return await authedFetch(`/api/admin/users/${encodeURIComponent(String(userId))}`, {
    method: "DELETE",
    authToken
  });
}

export async function getAdminStats({ authToken }) {
  return await authedFetch("/api/admin/stats", { authToken });
}

export async function resetUserPassword({ authToken, userId }) {
  return await authedFetch(`/api/admin/users/${encodeURIComponent(String(userId))}/reset-password`, {
    method: "POST",
    authToken,
    body: {}
  });
}

export async function verifyUserAccount({ authToken, userId }) {
  return await authedFetch(`/api/admin/users/${encodeURIComponent(String(userId))}/verify`, {
    method: "POST",
    authToken,
    body: {}
  });
}

export async function getUserAttempts({ authToken, userId, limit = 50 }) {
  const lim = Math.max(1, Math.min(500, Number(limit) || 50));
  return await authedFetch(`/api/admin/users/${encodeURIComponent(String(userId))}/attempts?limit=${encodeURIComponent(String(lim))}`, {
    authToken
  });
}

export async function getUserAuditLog({ authToken, userId, limit = 100 }) {
  const lim = Math.max(1, Math.min(500, Number(limit) || 100));
  return await authedFetch(`/api/admin/users/${encodeURIComponent(String(userId))}/audit-log?limit=${encodeURIComponent(String(lim))}`, {
    authToken
  });
}

export async function setUserPlan({ authToken, userId, tier }) {
  const t = tier === "pro" ? "pro" : "free";
  return await authedFetch(`/api/admin/users/${encodeURIComponent(String(userId))}/plan`, {
    method: "POST",
    authToken,
    body: { tier: t }
  });
}

export async function fetchAccountDeletionRequests({ authToken, status = "" }) {
  const qs = status ? `?status=${encodeURIComponent(String(status))}` : "";
  return await authedFetch(`/api/admin/account-deletions${qs}`, { authToken });
}

export async function approveAccountDeletionRequest({ authToken, requestId, executeNow = false }) {
  return await authedFetch(`/api/admin/account-deletions/${encodeURIComponent(String(requestId))}/approve`, {
    method: "POST",
    authToken,
    body: { executeNow: !!executeNow }
  });
}

export async function rejectAccountDeletionRequest({ authToken, requestId, reason = "" }) {
  return await authedFetch(`/api/admin/account-deletions/${encodeURIComponent(String(requestId))}/reject`, {
    method: "POST",
    authToken,
    body: { reason: String(reason || "") }
  });
}

export async function executeAccountDeletionRequest({ authToken, requestId }) {
  return await authedFetch(`/api/admin/account-deletions/${encodeURIComponent(String(requestId))}/execute`, {
    method: "POST",
    authToken,
    body: {}
  });
}

